import { Box, IconButton, TextField, Chip, Dialog, Paper } from '@mui/material';
import { styled } from '@mui/system';
import { recordingPulse, pulse } from '../animations';

export const ChatContainer = styled(Box)(() => ({
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#f8f9fa',
}));

export const MainContent = styled(Box)(() => ({
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
}));

export const InputContainer = styled(Box)(() => ({
    padding: '20px',
    backgroundColor: '#ffffff',
    borderTop: '1px solid #e9ecef',
}));

export const InputWrapper = styled(Box)(({theme}) => ({
    maxWidth: '700px',
    margin: '0 auto',
    position: 'relative',
    backgroundColor: '#f8f9fa',
    borderRadius: '12px',
    border: '2px solid transparent',
    transition: 'all 0.2s ease',
    '&:focus-within': {
        borderColor: theme.palette.primary.main,
        backgroundColor: '#ffffff',
        boxShadow: '0 0 0 3px rgba(88, 101, 242, 0.1)',
    }
}));

export const StyledTextField = styled(TextField)(() => ({
    '& .MuiOutlinedInput-root': {
        backgroundColor: 'transparent',
        borderRadius: '12px',
        fontSize: '0.95rem',
        border: 'none',
        '& fieldset': {
            border: 'none',
        },
        '&:hover fieldset': {
            border: 'none',
        },
        '&.Mui-focused fieldset': {
            border: 'none',
        }
    },
    '& .MuiOutlinedInput-input': {
        padding: '16px 120px 16px 20px',
        '&::placeholder': {
            opacity: 1,
        }
    }
}));

export const ButtonContainer = styled(Box)(() => ({
    position: 'absolute',
    right: '8px',
    top: '50%',
    transform: 'translateY(-50%)',
    display: 'flex',
    gap: '4px',
}));

export const ActionButton = styled(IconButton)(({theme, variant}) => ({
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    transition: 'all 0.2s ease',
    ...(variant === 'primary' && {
        backgroundColor: theme.palette.primary.main,
        color: 'white',
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
            transform: 'scale(1.05)',
        },
        '&:disabled': {
            backgroundColor: theme.palette.grey[300],
            color: theme.palette.grey[500],
        },
    }),
    ...(variant === 'recording' && {
        backgroundColor: theme.palette.error.main,
        color: 'white',
        animation: `${recordingPulse} 1s infinite`,
        '&:hover': {
            backgroundColor: theme.palette.error.dark,
        },
    }),
    ...(variant === 'secondary' && {
        backgroundColor: theme.palette.grey[200],
        color: theme.palette.text.primary,
        '&:hover': {
            backgroundColor: theme.palette.grey[300],
        },
    }),
}));

export const ModeSelector = styled(Box)(() => ({
    display: 'flex',
    gap: '8px',
    marginBottom: '16px',
    flexWrap: 'wrap',
}));

export const ModeChip = styled(Chip)(({theme, selected}) => ({
    borderRadius: '20px',
    fontWeight: 600,
    fontSize: '0.8rem',
    border: '2px solid',
    borderColor: selected ? theme.palette.primary.main : theme.palette.grey[300],
    backgroundColor: selected ? theme.palette.primary.main : 'transparent',
    color: selected ? 'white' : theme.palette.text.primary,
    '&:hover': {
        backgroundColor: selected ? theme.palette.primary.dark : theme.palette.grey[100],
        transform: 'translateY(-1px)',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    },
    transition: 'all 0.2s ease',
}));

export const RecordingDialog = styled(Dialog)(() => ({
    '& .MuiDialog-paper': {
        borderRadius: '20px',
        padding: '20px',
        minWidth: '400px',
    }
}));

export const WaveformContainer = styled(Box)(() => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '2px',
    height: '60px',
    margin: '20px 0',
}));

export const WaveBar = styled(Box)(({theme, amplitude}) => ({
    width: '4px',
    backgroundColor: theme.palette.primary.main,
    borderRadius: '2px',
    height: `${Math.max(4, amplitude * 60)}px`,
    transition: 'height 0.1s ease',
    animation: `${pulse} 1s infinite`,
}));

export const FileUploadArea = styled(Paper)(({theme}) => ({
    border: `2px dashed ${theme.palette.grey[300]}`,
    borderRadius: '12px',
    padding: '40px 20px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.primary.light + '10',
    },
    '&.drag-over': {
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.primary.light + '20',
    }
}));
